import {
  useCallback,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import t from 'react-translate';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import { MediaSourceType, requestMediaStream, stopMediaStreams } from 'recording/services/media-stream';
import { getAudioAnalyzer, getVolume } from 'recording/services/media-visualizer-helper';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import {
  startRolePlaySession,
  StartSessionPayload,
  setAiRolePlaySubmissionClosingReason,
} from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';
import { danger } from 'styles/global_defaults/colors';
import { RootState } from 'redux/schemas';

import {
  AIRolePlayStatus,
  SessionData,
  VoiceCardState,
  VoiceRecordingState,
} from '../ai-conversation-modal/context';
import streamAIResponse, { ClosingReason } from '../ai-conversation-modal/stream-ai-response';
import useTokenRefresh from './use-token-refresh';

// Countdown duration before the first AI turn starts
const COUNTDOWN_DURATION_MS = 8000;

// AbortController cancellations surface as a DOMException/Error with name='AbortError';
// the message varies across browsers, so always check .name.
const isAbortError = (err: unknown): boolean => (err as Error)?.name === 'AbortError';
// Silence after speech is detected that triggers end of learner turn
const POST_SPEECH_SILENCE_MS = 1000;
// Inactivity before any speech is detected — triggers null audio to AI
const PRE_SPEECH_SILENCE_MS = 5000;
// Hard cap: stop recording when base64-encoded audio would exceed this size (~300s at 16kHz mono)
const MAX_AUDIO_BASE64_BYTES = 5 * 1024 * 1024;
// Volume level below which we consider the mic silent
const SILENCE_THRESHOLD = 0.05;

interface UseVoiceAiConversationHandlersParams {
  audioStreamRef: MutableRefObject<MediaStream | null>
  recorderRef: MutableRefObject<RecordRTC | null>
  aiRolePlayId: number;
  setAiRolePlayStatus: Dispatch<SetStateAction<AIRolePlayStatus>>;
  setShowScenarioDetails: Dispatch<SetStateAction<boolean>>;
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  setIsTimeout: Dispatch<SetStateAction<boolean>>;
  abortControllerRef: MutableRefObject<AbortController | null>;
  setIsRolePlayStarting: Dispatch<SetStateAction<boolean>>;
  setMicrophoneEnabled: Dispatch<SetStateAction<boolean>>;
  setOpeningNarrative: Dispatch<SetStateAction<string>>;
}

interface UseVoiceAiConversationHandlersReturn {
  handleStartVoiceConversation: () => void;
  aiVoiceCardState: VoiceCardState;
  learnerVoiceCardState: VoiceCardState;
  learnerMediaStream: MediaStream | null;
  voiceRecordingState: VoiceRecordingState | null;
}

const useVoiceAiConversationHandlers = ({
  setAiRolePlayStatus,
  setShowScenarioDetails,
  sessionData,
  setSessionData,
  setIsTimeout,
  abortControllerRef,
  setIsRolePlayStarting,
  setMicrophoneEnabled,
  audioStreamRef,
  recorderRef,
  aiRolePlayId,
  setOpeningNarrative,
}: UseVoiceAiConversationHandlersParams): UseVoiceAiConversationHandlersReturn => {
  const dispatch = useDispatch();
  const currentCatalogId = useSelector((state: RootState) => state.app.currentCatalogId);

  const [aiVoiceCardState, setAiVoiceCardState] = useState(VoiceCardState.IDLE);
  const [learnerVoiceCardState, setLearnerVoiceCardState] = useState(VoiceCardState.IDLE);
  const [learnerMediaStream, setLearnerMediaStream] = useState<MediaStream | null>(null);
  const [voiceRecordingState, setVoiceRecordingState] = useState<VoiceRecordingState | null>(null);

  const hasReceivedNarrativeRef = useRef(false);

  // Session data ref avoids stale closures across async cycles
  const sessionDataRef = useRef<SessionData>(sessionData);
  sessionDataRef.current = sessionData;

  const submissionIdRef = useRef<number | null>(sessionData.submissionId);
  submissionIdRef.current = sessionData.submissionId;

  // Silence detection animation frame ref
  const silenceCheckFrameRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  const stopSilenceDetection = useCallback(() => {
    if (silenceCheckFrameRef.current !== null) {
      cancelAnimationFrame(silenceCheckFrameRef.current);
      silenceCheckFrameRef.current = null;
    }
  }, []);

  const setMicActive = useCallback((active: boolean) => {
    audioStreamRef.current?.getAudioTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign
      track.enabled = active;
    });
  }, [audioStreamRef]);

  const { ensureFreshToken } = useTokenRefresh({ sessionData, setSessionData });


  // Record learner audio with silence detection; resolves with base64-encoded WAV, or null if
  // no speech is detected within PRE_SPEECH_SILENCE_MS (triggers "are you there" flow).
  // Resolves after MAX_TURN_DURATION_MS regardless of speech/silence state.
  const recordLearnerTurn = useCallback((abortSignal: AbortSignal): Promise<string | null> => new Promise((resolve, reject) => {
    const stream = audioStreamRef.current;
    if (!stream) {
      reject(new Error('No audio stream'));
      return;
    }

    // Hoisted so ondataavailable can reference it before the full definition below
    let resolveWithAudio!: () => void;

    const recorder = new RecordRTC(stream, {
      type: 'audio',
      mimeType: 'audio/wav',
      recorderType: StereoAudioRecorder,
      desiredSampRate: 16000,
      numberOfAudioChannels: 1,
      disableLogs: true,
      timeSlice: 1000,
      ondataavailable: (blob: Blob) => {
        // blob.size is the cumulative WAV size; stop when its base64 would exceed the limit
        if (Math.ceil((blob.size * 4) / 3) >= MAX_AUDIO_BASE64_BYTES) {
          resolveWithAudio();
        }
      },
    });
    recorderRef.current = recorder;
    recorder.startRecording();

    let hasSpeechDetected = false;
    let lastSpeechTime = Date.now();
    let preSpeechSilenceTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanupTimers = () => {
      stopSilenceDetection();
      if (preSpeechSilenceTimer !== null) { clearTimeout(preSpeechSilenceTimer); preSpeechSilenceTimer = null; }
    };

    resolveWithAudio = () => {
      cleanupTimers();
      recorder.stopRecording(() => {
        recorder.getDataURL((url) => {
          resolve(url.split(',')[1]);
        });
      });
    };

    // 5s of silence before any speech → send null so AI prompts "are you still there?"
    const resolveWithSilence = () => {
      cleanupTimers();
      recorder.stopRecording(() => { resolve(null); });
    };

    preSpeechSilenceTimer = setTimeout(resolveWithSilence, PRE_SPEECH_SILENCE_MS);

    const checkSilence = () => {
      if (abortSignal.aborted) {
        cleanupTimers();
        recorder.stopRecording(() => {});
        reject(new DOMException('Recording aborted', 'AbortError'));
        return;
      }

      if (analyserRef.current) {
        const volume = getVolume(analyserRef.current);

        if (volume > SILENCE_THRESHOLD) {
          if (!hasSpeechDetected) {
            // First speech — cancel the pre-speech inactivity timer
            hasSpeechDetected = true;
            if (preSpeechSilenceTimer !== null) {
              clearTimeout(preSpeechSilenceTimer);
              preSpeechSilenceTimer = null;
            }
          }
          lastSpeechTime = Date.now();
        } else if (hasSpeechDetected && (Date.now() - lastSpeechTime) >= POST_SPEECH_SILENCE_MS) {
          resolveWithAudio();
          return;
        }
      }

      silenceCheckFrameRef.current = requestAnimationFrame(checkSilence);
    };

    silenceCheckFrameRef.current = requestAnimationFrame(checkSilence);
  }), [audioStreamRef, recorderRef, stopSilenceDetection]);

  const handleStartVoiceConversation = useCallback(async () => {
    setAiRolePlayStatus(AIRolePlayStatus.IN_PROGRESS);
    setIsRolePlayStarting(true);

    let stream: MediaStream | null = null;

    try {
      // 1. Request microphone access
      stream = await requestMediaStream(MediaSourceType.MIC);
      audioStreamRef.current = stream;
      setLearnerMediaStream(stream);

      // Set up AnalyserNode for silence detection (reused across turns)
      analyserRef.current = getAudioAnalyzer(stream);
      // Permission acquired; keep mic muted until it's the learner's turn
      setMicActive(false);

      // 2. Start role-play session to get JWT and endpoint
      const sessionRes = await wrapThunkAction(
        dispatch(startRolePlaySession({
          catalogId: currentCatalogId,
          aiRolePlayId,
        })),
      );

      const sessionPayload = sessionRes.payload as StartSessionPayload;
      if (!sessionPayload?.jwtToken || !sessionPayload?.aiApiEndpoint) {
        throw new Error('Invalid session payload');
      }

      const normalizedEndpoint = sessionPayload.aiApiEndpoint.replace(/\/+$/, '');
      const initialSessionData: SessionData = {
        aiApiEndpoint: normalizedEndpoint,
        jwtToken: sessionPayload.jwtToken,
        submissionId: sessionPayload.submissionId || null,
        expiresAt: sessionPayload.expiresAt || null,
      };

      setSessionData(initialSessionData);
      sessionDataRef.current = initialSessionData;
      submissionIdRef.current = sessionPayload.submissionId || null;

      setShowScenarioDetails(false);
      setIsTimeout(false);

      // 3. Show 8-second countdown before revealing AI card and starting the stream.
      // One session-scoped controller — never replaced during the session so unmount's abort()
      // always propagates. The local `signal` is captured here; the loop reads it directly rather
      // than going through the ref, so ref-nulling in unmount cleanup can't hide the abort.
      const sessionController = new AbortController();
      abortControllerRef.current = sessionController;
      const { signal } = sessionController;

      setVoiceRecordingState(VoiceRecordingState.COUNTDOWN);
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, COUNTDOWN_DURATION_MS);
        signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
      });

      if (signal.aborted) return;
      setVoiceRecordingState(VoiceRecordingState.RECORDING);
      setMicrophoneEnabled(true); // Triggers AI card to appear after countdown

      // 4. Run the voice conversation loop (iterative — avoids unbounded call-stack growth)
      // AI persona always speaks first, so the first turn carries no user audio.
      let isFirstTurn = true;
      let nextAudio: string | null = null;

      while (!signal.aborted) {
        // Refresh token before each API call
        const token = await ensureFreshToken(); // eslint-disable-line no-await-in-loop

        let hasFirstAudioDelta = false;
        let conversationClosingReason: ClosingReason | null = null;
        let hadStreamError = false;

        // Streaming audio playback via MediaSource — chunks are appended and played as they arrive.
        // SourceBuffer only allows one appendBuffer at a time, so incoming chunks queue up and are
        // flushed one-by-one on each 'updateend' event.
        let sourceBuffer: SourceBuffer | null = null;
        let isDoneAudioStreaming = false;
        let nextExpectedSeq = 0;
        const pendingAudioQueue = new Map<number, Uint8Array>();

        const mediaSource = new MediaSource();
        const objectUrl = URL.createObjectURL(mediaSource);
        const audioEl = new Audio(objectUrl);

        const tryFlush = () => {
          if (!sourceBuffer || sourceBuffer.updating) return;

          while (pendingAudioQueue.has(nextExpectedSeq)) {
            const bytes = pendingAudioQueue.get(nextExpectedSeq)!;
            pendingAudioQueue.delete(nextExpectedSeq);
            nextExpectedSeq += 1;
            sourceBuffer.appendBuffer(bytes);
            return; // appendBuffer is async; next append fires from 'updateend'
          }

          if (isDoneAudioStreaming && !sourceBuffer.updating) {
            try { mediaSource.endOfStream(); } catch { /* already ended or detached */ }
          }
        };

        const playbackDone = new Promise<void>((resolve) => {
          const cleanup = () => { URL.revokeObjectURL(objectUrl); resolve(); };
          audioEl.onended = cleanup;
          audioEl.onerror = cleanup;
          signal.addEventListener('abort', () => { audioEl.pause(); cleanup(); }, { once: true });
        });

        mediaSource.addEventListener('sourceopen', () => {
          sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
          sourceBuffer.addEventListener('updateend', tryFlush);
          tryFlush();
        });

        audioEl.play().catch(() => {});

        setAiVoiceCardState(VoiceCardState.THINKING);
        setLearnerVoiceCardState(VoiceCardState.IDLE);

        await streamAIResponse({ // eslint-disable-line no-await-in-loop
          aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
          token,
          // First turn: no learner audio yet; subsequent turns: send audio or null (silence)
          ...(isFirstTurn ? { userMessage: null } : { userAudio: nextAudio }),
          onChunk: (chunk) => {
            if (!hasReceivedNarrativeRef.current) {
              setOpeningNarrative((prev) => prev + chunk.content);
              if (chunk.isLastChunk) {
                hasReceivedNarrativeRef.current = true;
              }
            }
            if (chunk.closingReason) {
              conversationClosingReason = chunk.closingReason;
              if (submissionIdRef.current) {
                dispatch(setAiRolePlaySubmissionClosingReason({
                  aiRolePlaySubmissionId: submissionIdRef.current,
                  closingReason: chunk.closingReason,
                  aiRolePlayId,
                }));
              }
              setVoiceRecordingState(VoiceRecordingState.COMPLETED);
              setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);
            }
          },
          onAudioDelta: ({ audio, sequence }) => {
            const binary = atob(audio);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) {
              bytes[i] = binary.charCodeAt(i);
            }
            pendingAudioQueue.set(sequence, bytes);
            tryFlush();
            if (!hasFirstAudioDelta) {
              hasFirstAudioDelta = true;
              setAiVoiceCardState(VoiceCardState.SPEAKING);
            }
          },
          onAudioDone: () => {
            isDoneAudioStreaming = true;
            tryFlush();
          },
          onError: (err) => {
            if (!isAbortError(err)) {
              dispatch(addAlertMessage({
                type: AlertMessageType.WARNING,
                header: t.FORM.OOPS(),
                message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                customColor: danger,
              }));
            }
          },
          abortSignal: signal,
        }).catch((err) => {
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          if (!isAbortError(err)) hadStreamError = true;
        });

        if (signal.aborted || hadStreamError) break;

        // Conversation ended — exit the loop
        if (conversationClosingReason) {
          setAiVoiceCardState(VoiceCardState.IDLE);
          break;
        }

        // Wait for streaming audio playback to finish
        await playbackDone; // eslint-disable-line no-await-in-loop

        if (signal.aborted) break;

        // Learner's turn — enable mic only for recording, then mute again
        setAiVoiceCardState(VoiceCardState.IDLE);
        setLearnerVoiceCardState(VoiceCardState.SPEAKING);
        setMicActive(true);

        nextAudio = await recordLearnerTurn(signal); // eslint-disable-line no-await-in-loop
        isFirstTurn = false;

        setMicActive(false);

        if (signal.aborted) break;

        setLearnerVoiceCardState(VoiceCardState.IDLE);
      }
    } catch (error) {
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        setMicrophoneEnabled(false);
      } else if (!isAbortError(error)) {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.FORM.OOPS(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
          customColor: danger,
        }));
      }
      setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
      setAiVoiceCardState(VoiceCardState.IDLE);
      setLearnerVoiceCardState(VoiceCardState.IDLE);
      setVoiceRecordingState(null);
    } finally {
      // Always release all audio resources regardless of how the session ended
      stopSilenceDetection();
      if (audioStreamRef.current) {
        stopMediaStreams(audioStreamRef.current);
        audioStreamRef.current = null;
      }
      if (recorderRef.current) {
        recorderRef.current.destroy();
        recorderRef.current = null;
      }
      analyserRef.current = null;
      setLearnerMediaStream(null);
      setIsRolePlayStarting(false);
    }
  }, [
    dispatch,
    currentCatalogId,
    aiRolePlayId,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    setSessionData,
    setIsTimeout,
    setIsRolePlayStarting,
    setMicrophoneEnabled,
    audioStreamRef,
    recorderRef,
    abortControllerRef,
    stopSilenceDetection,
    ensureFreshToken,
    recordLearnerTurn,
    setMicActive,
    setOpeningNarrative,
  ]);

  return {
    handleStartVoiceConversation,
    aiVoiceCardState,
    learnerVoiceCardState,
    learnerMediaStream,
    voiceRecordingState,
  };
};

export default useVoiceAiConversationHandlers;
